import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight, ArrowLeft, X } from "lucide-react";
import Loader from "../loader";
import Helmet from "react-helmet";
import LazyOnScroll from "../../ui/LazyOnScroll";

interface ProjectDetail {
  _id: string;
  title: string;
  category: string;
  location: string;
  clientName?: string;
  details: {
    gallery: { asset: { url: string; _id: string } }[];
    videos?: { asset: { url: string; _id: string } }[];
  };
}

export default function ProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const minSwipeDistance = 50;

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    import(`../../data/projects/${id}.json`)
      .then((module) => {
        setProjectDetail(module.default);
        setCurrentImageIndex(0);
      })
      .catch(() => {
        setProjectDetail(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (!projectDetail) {
      setImagesLoaded(false);
      return;
    }

    const gallery = projectDetail.details.gallery;
    if (gallery.length === 0) {
      setImagesLoaded(true);
      return;
    }

    setImagesLoaded(false); // Reset imagesLoaded when gallery changes

    let loaded = 0;
    gallery.forEach(({ asset }) => {
      const img = new Image();
      img.src = asset.url;
      img.onload = () => {
        loaded += 1;
        if (loaded === gallery.length) {
          setImagesLoaded(true);
        }
      };
      img.onerror = () => {
        loaded += 1;
        if (loaded === gallery.length) {
          setImagesLoaded(true);
        }
      };
    });
  }, [projectDetail]);

  const nextImage = useCallback(() => {
    if (!projectDetail) return;
    setCurrentImageIndex((prev) =>
      prev === projectDetail.details.gallery.length - 1 ? 0 : prev + 1
    );
  }, [projectDetail]);

  const prevImage = useCallback(() => {
    if (!projectDetail) return;
    setCurrentImageIndex((prev) =>
      prev === 0 ? projectDetail.details.gallery.length - 1 : prev - 1
    );
  }, [projectDetail]);

  const nextLightboxImage = useCallback(() => {
    if (!projectDetail) return;
    setLightboxIndex((prev) =>
      prev === projectDetail.details.gallery.length - 1 ? 0 : prev + 1
    );
  }, [projectDetail]);

  const prevLightboxImage = useCallback(() => {
    if (!projectDetail) return;
    setLightboxIndex((prev) =>
      prev === 0 ? projectDetail.details.gallery.length - 1 : prev - 1
    );
  }, [projectDetail]);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") nextLightboxImage();
      else if (e.key === "ArrowLeft") prevLightboxImage();
      else if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, nextLightboxImage, prevLightboxImage]);

  const onCarouselTouchStart = (e: React.TouchEvent) => {
    setTouchEndX(null);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onCarouselTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const onCarouselTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return;
    const distance = touchStartX - touchEndX;
    if (Math.abs(distance) > minSwipeDistance) {
      distance > 0 ? nextImage() : prevImage();
    }
    setTouchStartX(null);
    setTouchEndX(null);
  };

  const [lightboxTouchStartX, setLightboxTouchStartX] = useState<number | null>(
    null
  );
  const [lightboxTouchEndX, setLightboxTouchEndX] = useState<number | null>(
    null
  );

  const onLightboxTouchStart = (e: React.TouchEvent) => {
    setLightboxTouchEndX(null);
    setLightboxTouchStartX(e.targetTouches[0].clientX);
  };

  const onLightboxTouchMove = (e: React.TouchEvent) => {
    setLightboxTouchEndX(e.targetTouches[0].clientX);
  };

  const onLightboxTouchEnd = () => {
    if (lightboxTouchStartX === null || lightboxTouchEndX === null) return;
    const distance = lightboxTouchStartX - lightboxTouchEndX;
    if (Math.abs(distance) > minSwipeDistance) {
      distance > 0 ? nextLightboxImage() : prevLightboxImage();
    }
    setLightboxTouchStartX(null);
    setLightboxTouchEndX(null);
  };

  if (loading || !imagesLoaded) {
    return <Loader />;
  }

  if (!projectDetail) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-center px-4">
        <h2 className="text-2xl font-bold mb-4">Project not found</h2>
        <Button 
          onClick={() => navigate("/portfolio")} 
          className="h-12 px-6 bg-[#484331] hover:bg-[#3e3a2b] text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
        >
          Back to Portfolio
        </Button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Portfolio – {projectDetail.title}</title>
        <meta
          name="description"
          content={`Located in ${projectDetail.location}`}
        />
        <meta property="og:image" content="/logo.jpg" />
      </Helmet>
      <div className="flex min-h-screen flex-col transition-opacity duration-700">
        <main className="flex-1">
          <LazyOnScroll>
            <section className="py-6 bg-muted/50">
              <div className="container max-w-screen-2xl mx-auto px-4 md:px-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <Link
                    to="/portfolio"
                    className="mb-4 inline-flex items-center text-sm text-muted-foreground md:mb-0"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back to Projects
                  </Link>
                  <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
                    {projectDetail.title || "title"}
                  </h1>
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  {projectDetail.category} • {projectDetail.location} •{" "}
                  {projectDetail.clientName || ""}
                </div>
              </div>
            </section>
          </LazyOnScroll>

          <LazyOnScroll delay={80}>
            <section className="relative py-6">
              <div className="container max-w-screen-2xl mx-auto px-4 md:px-6">
                {projectDetail.details.gallery.length === 0 ? (
                  <p className="text-center text-muted-foreground">
                    No images available.
                  </p>
                ) : (
                  <div className="relative overflow-hidden rounded-lg max-h-[70vh]">
                    <img
                      src={
                        projectDetail.details.gallery[currentImageIndex].asset
                          .url
                      }
                      alt={`Gallery ${currentImageIndex + 1}`}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/placeholder.svg";
                      }}
                      className="w-full h-[70vh] object-contain cursor-pointer rounded-lg"
                      onClick={() => openLightbox(currentImageIndex)}
                      onTouchStart={onCarouselTouchStart}
                      onTouchMove={onCarouselTouchMove}
                      onTouchEnd={onCarouselTouchEnd}
                    />

                    <Button
                      variant="ghost"
                      className="absolute left-4 top-1/2 -translate-y-1/2"
                      onClick={prevImage}
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </Button>
                    <Button
                      variant="ghost"
                      className="absolute right-4 top-1/2 -translate-y-1/2"
                      onClick={nextImage}
                    >
                      <ChevronRight className="h-6 w-6" />
                    </Button>
                  </div>
                )}
              </div>
            </section>
          </LazyOnScroll>

          {projectDetail.details.gallery.length > 1 && (
            <section className="container max-w-screen-2xl mx-auto px-4 md:px-6 mb-12">
              <div className="flex space-x-3 overflow-x-auto scrollbar-hide">
                {projectDetail.details.gallery.map((img, index) => (
                  <img
                    key={img.asset._id || index}
                    src={img.asset.url}
                    alt={`Thumbnail ${index + 1}`}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`h-20 w-28 object-cover rounded cursor-pointer transition-transform duration-200 ${
                      index === currentImageIndex
                        ? "ring-2 ring-[#484331] scale-105"
                        : "opacity-70 hover:opacity-100"
                    }`}
                  />
                ))}
              </div>
            </section>
          )}

          {lightboxOpen && projectDetail.details.gallery.length > 0 && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
              <Button
                variant="ghost"
                className="absolute top-4 right-4 text-white"
                onClick={closeLightbox}
              >
                <X className="h-6 w-6" />
              </Button>
              <Button
                variant="ghost"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white"
                onClick={prevLightboxImage}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <img
                src={projectDetail.details.gallery[lightboxIndex].asset.url}
                alt={`Gallery ${lightboxIndex + 1}`}
                className="max-h-[80vh] w-auto rounded-lg"
                onTouchStart={onLightboxTouchStart}
                onTouchMove={onLightboxTouchMove}
                onTouchEnd={onLightboxTouchEnd}
              />
              <Button
                variant="ghost"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white"
                onClick={nextLightboxImage}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </div>
          )}

          {projectDetail.details.videos &&
            projectDetail.details.videos.length > 0 && (
              <section className="py-6">
                <div className="container max-w-screen-2xl mx-auto px-4 md:px-6">
                  <h2 className="text-2xl font-bold mb-4">Project Videos</h2>
                  <div className="space-y-6">
                    {projectDetail.details.videos.map((video, i) => (
                      <div
                        key={video.asset._id || i}
                        className="aspect-w-16 aspect-h-9"
                      >
                        <iframe
                          src={video.asset.url}
                          title={`Project video ${i + 1}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full rounded-lg"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
        </main>
      </div>
    </>
  );
}
